
// frontend/src/components/Login.js

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../axiosConfig';
import { Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import AuthLanguageSelector from './AuthLanguageSelector';
import '../index.css';

function Login({ setToken }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation();

  // Login.js
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(
        '/api/users/login',
        { username, password },
        { skipAuthSessionHandling: true }
      );
      const { token } = response.data;

      // Just set the token - App.js will handle the rest
      localStorage.setItem('token', token);
      // localStorage.setItem('userRole', user.role);  // Store role in localStorage too
      setToken(token);
      // setUserRole(user.role); // Set the role before navigation
      // setEntityContext(user.entityId); // If you're using entity context

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          // Check if there's a specific error message from the server
          const errorKey = error.response.data?.error;
          if (errorKey && errorKey === 'invalidCredentials') {
            setErrorMessage(t('invalidCredentials'));
          } else if (errorKey) {
            // Try to translate the error key, fallback to the key itself if no translation exists
            setErrorMessage(t(errorKey, errorKey));
          } else {
            setErrorMessage(t('invalidCredentials'));
          }
        } else if (error.response.status === 403 && error.response.data.code === 'USER_NOT_ACTIVE') {
          // Handle account status errors
          const status = error.response.data.status;
          setErrorMessage(t(`accountStatus.${status}`));
        } else if (error.response.data?.error) {
          // Check if error message is a translation key
          const errorKey = error.response.data.error;
          if (errorKey.startsWith('accountStatus.') || errorKey.startsWith('entityStatus.')) {
            setErrorMessage(t(errorKey));
          } else {
            // Try to translate the key, fallback to the key itself
            setErrorMessage(t(errorKey, errorKey));
          }
        } else {
          setErrorMessage(t('loginFailed'));
        }
      } else {
        // Network error or no response - don't show technical error messages
        setErrorMessage(t('loginFailed'));
      }
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Clear the error message after 5 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  return (
    <form onSubmit={handleSubmit} className="max-w-sm mx-auto mt-8 p-6 bg-gray-100 dark:bg-gray-800 shadow-md rounded-lg">
      <div className="mb-4 flex justify-end">
        <AuthLanguageSelector />
      </div>
      <h2 className="text-2xl font-bold mb-4 text-center text-blue-600">{t('login')}</h2>
      <div className="mb-4">
        <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('username')}:
        </label>
        <input
          type="text"
          id="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          placeholder={t('enterUsername')}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 hover:border-blue-700 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
        />
      </div>
      <div className="mb-6 relative">
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('password')}:
        </label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            value={password}
            placeholder={t('enterPassword')}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 hover:border-blue-700 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
          />
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            tabIndex="-1"
          >
            {showPassword ? (
              <EyeOff className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Eye className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>
      <button 
        type="submit" 
        className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-200"
      >
        {t('login')}
      </button>
      <div className="flex justify-center items-center mt-4 text-sm">
        <p>
          <Link 
            to="/forgot-password" 
            className="font-medium text-blue-600 dark:text-blue-400 hover:underline transition-colors duration-300"
          >
            {t('forgotPassword')}
          </Link>
        </p>
      </div>
      <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-300">
        <p>
          {t('noAccount')} {t('contactAdminForInvite')}
        </p>
      </div>
      {errorMessage && <p className="text-red-500 mb-2">{errorMessage}</p>} 
    </form>
  );
};

export default Login;
